// frontend/app/components/lessonPlans/teachsense/TeachSenseLive.js
//
// Where the class is, right now: who is in which section, who has finished,
// who has not opened it.
//
// The section is drawn from SECTION_META (phases are a hidden key), so the
// colour beside a student's name is the colour of the section on the rail the
// class is looking at. Rows arrive
// furthest-behind first and are rendered in the order they arrive — the server
// decided who needs looking at, and re-sorting here would quietly overrule it.

import React from "react";
import { StyleSheet, Text, View } from "react-native";

import { getSectionForPhase, getSectionMeta, tint } from "../lessonPlanTheme";
import { Pill } from "../blocks/LessonBlockShell";
import { MAX_ROWS, Section, StudentRow } from "./TeachSenseSection";
import { fill, relativeTime } from "./teachsenseText";

// Hexes rather than tokens because tint() blends a wash from one. The green
// is what `success` resolves to and the slate is the neutral the unanswered
// band uses; a student mid-lesson borrows the accent of the phase they are on.
const DONE_HEX = "#10b981";
const NOT_STARTED_HEX = "#64748b";

const LiveRow = ({ entry, colors, isDarkMode, divided, t }) => {
  const phaseKey = String(entry?.phase || "").toLowerCase();
  // Teachers see the four sections, never the phase a student is on.
  const sectionKey = phaseKey ? getSectionForPhase(phaseKey) : null;
  const phase = sectionKey ? getSectionMeta(sectionKey) : null;
  const completed = entry?.completed === true;
  const stepCount = Math.max(0, Number(entry?.step_count) || 0);
  // `step_index` is zero-based on the wire; a teacher counts from one.
  const stepIndex = Math.max(0, Number(entry?.step_index) || 0);
  const since = relativeTime(entry?.last_activity_at, t);

  // No phase and no activity is not "on step 1" — it is a student who has not
  // opened the lesson, and the two call for opposite responses.
  const started = Boolean(phaseKey) || Boolean(entry?.last_activity_at);

  const accent = completed ? colors.success : started ? colors.textSecondary : colors.textMuted;
  const hex = completed ? DONE_HEX : phase ? phase.accent : NOT_STARTED_HEX;

  const position =
    stepCount > 0
      ? fill(t("Step {n} of {total}"), { n: Math.min(stepIndex + 1, stepCount), total: stepCount })
      : "";
  const stepLabel = String(entry?.last_active_step_key || "");

  const status = completed
    ? t("Completed")
    : started
      ? position
      : t("Not started yet");

  return (
    <StudentRow
      entry={entry}
      colors={colors}
      isDarkMode={isDarkMode}
      accent={accent}
      hex={hex}
      divided={divided}
      detail={[status, since].filter(Boolean).join(", ")}
      trailing={
        completed ? (
          <Pill
            colors={colors}
            label={t("Done")}
            icon="check-circle-outline"
            accent={colors.success}
            background={tint(DONE_HEX, isDarkMode, 1.4)}
          />
        ) : phase ? (
          <Pill
            colors={colors}
            label={t(phase.label)}
            icon={phase.icon}
            accent={phase.accent}
            background={tint(phase.accent, isDarkMode, 1.4)}
          />
        ) : null
      }
    >
      <Text style={[styles.status, { color: started ? colors.textSecondary : colors.textMuted }]}>
        {[status, stepLabel && !completed ? stepLabel : "", since]
          .filter(Boolean)
          .join(" · ")}
      </Text>
    </StudentRow>
  );
};

const TeachSenseLive = ({
  live = null,
  meta = null,
  colors,
  isDarkMode = false,
  isNarrow = false,
  t,
}) => {
  const translate = t || ((value) => value);
  const entries = Array.isArray(live) ? live : [];
  const shown = entries.slice(0, MAX_ROWS);
  const overflow = entries.length - shown.length;

  const roster = Math.max(0, Number(meta?.student_count) || 0);
  const started = Math.max(0, Number(meta?.students_started) || 0);
  const completed = Math.max(0, Number(meta?.students_completed) || 0);

  return (
    <Section
      testID="teachsense-live"
      colors={colors}
      isNarrow={isNarrow}
      icon="access-point"
      accent={colors.info}
      title={translate("Where the class is")}
      subtitle={fill(
        translate("{started} of {roster} started · {completed} completed"),
        { started, roster, completed }
      )}
      count={entries.length || null}
      isEmpty={entries.length === 0}
      empty={translate("Nobody has opened this lesson yet.")}
    >
      {shown.map((entry, index) => (
        <LiveRow
          key={entry.student_email || `live-${index}`}
          entry={entry}
          colors={colors}
          isDarkMode={isDarkMode}
          divided={index > 0}
          t={translate}
        />
      ))}
      {overflow > 0 ? (
        <Text style={[styles.more, { color: colors.textMuted }]}>
          {fill(translate("+{n} more students"), { n: overflow })}
        </Text>
      ) : null}
    </Section>
  );
};

const styles = StyleSheet.create({
  status: { fontSize: 11, fontWeight: "500", lineHeight: 16, marginTop: 2 },
  more: { fontSize: 12, fontStyle: "italic", paddingTop: 10 },
});

export default TeachSenseLive;
